import { API_BASE, USE_REAL_API, apiUrl } from '../lib/api'
import { useAuthStore } from '../store/auth'
import { isProblemReportApiUrl } from '../lib/problem-reporting/registry'
import { clearReportIdentity, reportRequestContext, settleReportRequest } from '../lib/problem-reporting/context'

/**
 * orval mutator — used by codegen'd hooks to issue HTTP calls.
 * Routes through lib/api.ts so MSW SW scope is respected in /m0/ prod deploy.
 *
 * M0/M1/M2: requests are MSW-intercepted; M3+ real BE responds.
 */
export async function orvalClient<T>(
  generatedUrl: string,
  options: RequestInit = {},
): Promise<T> {
  const path = generatedUrl.replace(/^\/?api\/v1/, '')
  const url = apiUrl(path)

  const report = isProblemReportApiUrl(url, API_BASE, typeof window === 'undefined' ? 'http://localhost' : window.location.origin)

  // Attach the real-auth bearer token when present (set after a real login).
  // In maket/simulation mode no token is set, so the header is omitted and MSW
  // handles the request unchanged.
  const token = useAuthStore.getState().accessToken
  const headers = new Headers(options.headers)
  const context = report ? reportRequestContext(headers.get('X-Problem-Report-Context')) : null
  headers.delete('X-Problem-Report-Context')
  headers.delete('X-Pexify-Simulated-Supply-Persona')
  if (context) {
    headers.delete('X-Pexify-Simulated-Surface')
    headers.delete('X-Pexify-Simulated-User')
    headers.set('X-Problem-Report-Context', context.identity.membershipId)
    if (!USE_REAL_API) {
      headers.set('X-Pexify-Simulated-Surface', context.identity.shell)
      if (context.identity.simulatedSupplyPersona) headers.set('X-Pexify-Simulated-Supply-Persona', context.identity.simulatedSupplyPersona)
    }
  }
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const rejectTransport = async (error: unknown): Promise<never> => {
    if (context && !(error instanceof Error && error.name === 'AbortError')) await settleReportRequest(context, options.signal)
    throw error
  }
  // Only the server can distinguish a fresh write from an accepted receipt replay while rollout is off.
  const res = await fetch(url, {
    ...options,
    headers,
    ...(context ? { redirect: 'error' as const } : {}),
  }).catch(rejectTransport)

  if (!res.ok) {
    const problem = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
    if (context) {
      await settleReportRequest(context, options.signal)
      if (res.status === 409 && problem.code === 'report_context_changed') clearReportIdentity('unavailable')
    }
    throw Object.assign(new Error(problem.detail ?? `HTTP ${res.status}`), {
      status: res.status,
      problem,
      retryAfter: res.headers.get('Retry-After'),
    })
  }
  const data = await res.json().catch(rejectTransport)
  if (context) await settleReportRequest(context, options.signal)
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as T
}
